import { useEffect, useMemo, useRef, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ForgeButton } from "@/components/ui/forge-button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { SelectOption } from "@/components/ui/select"
import { PolicyListRow } from "@/components/domain/policy-list-row"
import { PolicyEditorPanel } from "@/components/domain/policy-editor-panel"
import { EscalationPolicyDesignerPane } from "@/components/domain/escalation-policy-designer-pane"
import { VersionTimeline } from "@/components/domain/version-timeline"
import { fmtCompactRelative } from "@/lib/format"
import type {
  EscalationPoliciesIndexProps,
  EscalationPolicy,
} from "@/types/escalation_policy"

// /escalation_policies — two-column list+editor layout for Auto Triage Policies.
//
// Left column: filterable list of rules (PolicyListRow). First-match-wins
// ordering communicated by drag-handle + position. Selected row gets a red
// ring + tinted background.
//
// Right column: the focused rule's editor (PolicyEditorPanel). Local-state
// buffer; Cancel reverts; Save PATCHes. Toggle/delete also flow through
// the same use-case-style handlers on this page so list-row controls stay
// in sync.
//
// State model:
//   - The presenter sends `activeSet` = whatever the operator is currently
//     viewing (live or draft). `policies` is already that set's rows.
//   - When a draft exists, the user can flip between live + draft via the
//     "Switch to live/draft" link in the banner. We re-fetch via Inertia
//     (?view=...) so the server can return the right slice.
//   - All mutations target the draft. Editor controls are gated on
//     `editable = activeSet.state === "draft"`.

const BASE = "/escalation_policies"

const STATUS_FILTER_OPTIONS: SelectOption<string>[] = [
  { value: "active",   label: "Active rules" },
  { value: "archived", label: "Show archived" },
  { value: "all",      label: "All rules" },
]

export default function EscalationPoliciesIndex({
  activeSet,
  liveSet,
  draftSet,
  lineage,
  policies,
  diff,
  users,
  agents,
  reasonOptions,
  topicOptions,
  agentPools,
  ticketQueues,
  customFieldCatalog,
  policyCapabilities,
}: EscalationPoliciesIndexProps) {
  const [search, setSearch] = useState("")
  // Default to "all" so inactive rows stay visible in the list — they
  // render dimmed via PolicyListRow's opacity rather than disappearing.
  // Operators can still narrow with the filter dropdown.
  const [statusFilter, setStatusFilter] = useState<"active" | "archived" | "all">("all")
  const [designerOpen, setDesignerOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(policies[0]?.id ?? null)

  // Track previous policy ids so we can auto-select a freshly-added rule.
  const prevIdsRef = useRef<Set<number>>(new Set(policies.map((p) => p.id)))

  // After Inertia reloads (props change): if the selected id is gone,
  // fall back to the first row. If a brand-new id appeared, prefer it.
  useEffect(() => {
    const ids = new Set(policies.map((p) => p.id))
    const newlyAdded = policies.find((p) => !prevIdsRef.current.has(p.id))
    if (newlyAdded) {
      setSelectedId(newlyAdded.id)
    } else if (selectedId == null || !ids.has(selectedId)) {
      setSelectedId(policies[0]?.id ?? null)
    }
    prevIdsRef.current = ids
  }, [policies]) // eslint-disable-line react-hooks/exhaustive-deps

  const editable = activeSet?.state === "draft"
  const viewing: "live" | "draft" = activeSet?.state === "draft" ? "draft" : "live"

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return policies.filter((p) => {
      if (term && !p.name.toLowerCase().includes(term)) return false
      if (statusFilter === "active"   && !p.active) return false
      if (statusFilter === "archived" &&  p.active) return false
      return true
    })
  }, [policies, search, statusFilter])

  const selectedPolicy = useMemo(
    () => policies.find((p) => p.id === selectedId) ?? null,
    [policies, selectedId],
  )

  const selectedRuleNumber = useMemo(() => {
    if (!selectedPolicy) return 0
    const idx = policies.findIndex((p) => p.id === selectedPolicy.id)
    return idx >= 0 ? idx + 1 : selectedPolicy.position + 1
  }, [policies, selectedPolicy])

  // Lifetime match total across the whole set, used to compute each
  // row's share-of-matches in the upper-right counter.
  const setTotalMatches = useMemo(
    () => policies.reduce((sum, p) => sum + (p.timesMatched ?? 0), 0),
    [policies],
  )

  // -- mutations -----------------------------------------------------------

  const updatePolicy = (id: number, body: Partial<EscalationPolicy>) => {
    router.patch(
      `${BASE}/${id}`,
      { escalation_policy: body } as unknown as Record<string, never>,
      { preserveScroll: true },
    )
  }
  const deletePolicy = (id: number) => {
    // Pick a fallback row to focus once the active one disappears.
    const idx = policies.findIndex((p) => p.id === id)
    const next = policies[idx + 1] ?? policies[idx - 1] ?? null
    setSelectedId(next?.id ?? null)
    router.delete(`${BASE}/${id}`, { preserveScroll: true })
  }
  const togglePolicyActive = (id: number, active: boolean) => {
    updatePolicy(id, { active })
  }
  const addRule = () => {
    router.post(BASE, {
      escalation_policy: {
        name:     "New rule",
        position: policies.length,
        active:   true,
      },
    }, { preserveScroll: true })
  }
  // Drag-drop reorder: caller passes the policy id being dragged and the
  // index it was dropped on. We reorder the FULL list (not the filtered
  // view) so an active-rules-only filter doesn't accidentally reorder
  // archived rows. POST returns 200 + the controller refreshes via
  // Inertia, so the new positions show up on the next render.
  const reorderPolicies = (fromId: number, toIndex: number) => {
    if (!editable) return
    const fullOrder = policies.map((p) => p.id)
    const fromIdx = fullOrder.indexOf(fromId)
    if (fromIdx < 0 || fromIdx === toIndex) return
    const next = [...fullOrder]
    next.splice(fromIdx, 1)
    next.splice(toIndex, 0, fromId)
    router.post(`${BASE}/reorder`, { ids: next }, {
      preserveScroll: true,
      preserveState:  true,
    })
  }
  const switchView = (next: "live" | "draft") => {
    router.get(BASE, { view: next }, { preserveScroll: true, preserveState: false })
  }
  const startDraft = () => {
    router.post(BASE, {}, { preserveScroll: true })
  }
  const promote = () => {
    if (!confirm("Promote this draft to live? The current live set will be archived.")) return
    router.post(`${BASE}/promote`, {}, { preserveScroll: true })
  }
  const discardDraft = () => {
    if (!confirm("Discard the current draft? All unpromoted changes will be lost.")) return
    router.post(`${BASE}/discard_draft`, {}, { preserveScroll: true })
  }
  // Click an archived dot → load THAT version of the set in read-only
  // mode. Versioning is managed at the SET level, so we route by version
  // number, mirroring how Skills handle it.
  const selectVersion = (setId: number) => {
    const target = lineage.find((v) => v.id === setId)
    if (!target) return
    router.get(BASE, { version: target.version }, { preserveScroll: true, preserveState: false })
  }

  // -- render --------------------------------------------------------------

  const hasDraft   = draftSet !== null
  const totalDiff  = diff.total_changes ?? 0
  // activeSet can be null in fresh tenants with no policy sets at all;
  // the page later renders an empty-state. Earlier reads (lines 88-89)
  // already used `activeSet?.state` — keep the same defense here.
  const archivedView = activeSet?.state === "archived"

  return (
    <AppShell>
      {/* Page header — search/filter on the left of the action cluster,
          Edit-with-Forge sits next to the red New Draft button on the
          right per the routing-rules mockup. The full diff banner has
          been removed; promote / discard show inline only when a draft
          exists and has unpromoted changes. */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end md:justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-app-muted">Settings</div>
          <h1 className="text-xl font-semibold text-app-text">Routing rules</h1>
          <p className="mt-1 max-w-2xl text-xs text-app-muted">
            Rules are evaluated top-to-bottom and the first match wins.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:flex md:flex-wrap md:items-center">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rules…"
            className="w-full md:w-56"
          />
          <div className="w-full sm:min-w-[160px] md:w-auto">
            <Select<string>
              value={STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter) ?? null}
              onChange={(o) => o && setStatusFilter(o.value as typeof statusFilter)}
              options={STATUS_FILTER_OPTIONS}
              isSearchable={false}
            />
          </div>
          <ForgeButton
            size="md"
            onClick={() => {
              if (!hasDraft) {
                router.post(BASE, {}, { preserveScroll: true, onSuccess: () => setDesignerOpen(true) })
              } else {
                setDesignerOpen(true)
              }
            }}
            disabled={!policyCapabilities.canEdit}
            title={!policyCapabilities.canEdit ? "Editing requires agent_manager or admin" : "Edit with Forge"}
            className="w-full md:w-auto"
          >
            Edit with Forge
          </ForgeButton>
          <Button
            variant="red"
            size="md"
            onClick={() => {
              // Only seed a starter rule when the draft is empty — otherwise
              // clicking the button shouldn't pile up "New rule" rows on top
              // of the operator's existing draft. Once a draft has at least
              // one row, the button no-ops (disabled state below makes that
              // explicit).
              if (!hasDraft) startDraft()
              else if (policies.length === 0) addRule()
            }}
            disabled={
              !policyCapabilities.canEdit ||
              archivedView ||
              (hasDraft && policies.length > 0)
            }
            title={
              !policyCapabilities.canEdit ? "Editing requires agent_manager or admin"
              : archivedView ? "Switch back to live to start a new draft"
              : (hasDraft && policies.length > 0)
                ? "A draft is already in progress — promote or discard it before starting fresh"
              : hasDraft
                ? "Seed the empty draft with a starter rule"
                : "Clone live → draft and start editing"
            }
            className="w-full md:w-auto"
          >
            + New Draft
          </Button>
        </div>
      </div>

      {lineage.length > 1 && activeSet && (
        <Card className="mb-3 px-3 py-2">
          <VersionTimeline
            versions={lineage}
            selectedVersionId={activeSet.id}
            onSelect={selectVersion}
          />
        </Card>
      )}

      {/* Read-only callout only — surfaces when an operator clicked an
          archived dot in the timeline. The promote/discard strip below
          handles the live-vs-draft state. */}
      {archivedView && (
        <Card className="mb-3 px-4 py-2 text-xs text-app-muted">
          <span className="rounded-control border border-border bg-white/5 px-2 py-0.5 text-[11px] uppercase tracking-wide">
            Read-only
          </span>{" "}
          Viewing v{activeSet.version} (archived).{" "}
          <button type="button" onClick={() => switchView("live")} className="text-brand-red hover:underline">
            Back to live
          </button>
        </Card>
      )}

      {/* Draft action strip — only renders when a draft exists, replacing
          the old PolicyDiffBanner. Lets ops switch view, promote, or
          discard without taking up the full banner area. */}
      {!archivedView && hasDraft && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-card border border-border bg-app-panel2/40 px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-app-muted">Viewing:</span>
            <button
              type="button"
              onClick={() => switchView("live")}
              className={viewing === "live" ? "font-medium text-app-text" : "text-app-muted hover:text-app-text"}
            >
              Live
            </button>
            <span className="text-app-subtle">·</span>
            <button
              type="button"
              onClick={() => switchView("draft")}
              className={viewing === "draft" ? "font-medium text-status-warning" : "text-app-muted hover:text-app-text"}
            >
              Draft
            </button>
            <span className="text-app-muted">
              {totalDiff === 0 ? "no changes vs live" : totalDiff === 1 ? "1 change vs live" : `${totalDiff} changes vs live`}
            </span>
            {liveSet?.promoted_at && (
              <span className="text-app-subtle">
                · live promoted {fmtCompactRelative(liveSet.promoted_at)}
                {liveSet.promoted_by ? ` by ${liveSet.promoted_by}` : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="red"
              size="sm"
              onClick={promote}
              disabled={!policyCapabilities.canPromote || totalDiff === 0 || viewing === "live"}
              title={
                !policyCapabilities.canPromote ? "Promote requires admin"
                : viewing === "live"           ? "Switch to the draft to promote"
                : totalDiff === 0              ? "No changes to promote"
                                               : "Promote draft to live"
              }
            >
              Promote
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={discardDraft}
              disabled={!policyCapabilities.canEdit || viewing === "live"}
              title={
                !policyCapabilities.canEdit ? "Editing requires agent_manager or admin"
                : viewing === "live"  ? "Switch to the draft to discard it"
                                      : "Discard the current draft"
              }
            >
              Discard draft
            </Button>
          </div>
        </div>
      )}

      {/* Two-column list+editor */}
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* Left: rule list */}
        <Card className="flex flex-col p-0">
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
            <div className="text-sm font-semibold text-app-text">
              Rules <span className="text-app-muted">({filtered.length})</span>
            </div>
            {editable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={addRule}
                disabled={!policyCapabilities.canEdit}
                title="Append a new rule to the bottom of the draft"
              >
                + Add rule
              </Button>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-app-muted">
              {policies.length === 0 ? (
                <>
                  <div>
                    No triage rules yet. Click{" "}
                    <span className="font-medium text-app-text">+ New Draft</span>{" "}
                    or ask Forge to set things up.
                  </div>
                  <div className="mt-2 text-xs text-app-subtle">
                    Tip: define your operator groups under{" "}
                    <Link href="/agent_pools" className="text-brand-red hover:underline">
                      Support Pools
                    </Link>{" "}
                    first, then reference them here.
                  </div>
                </>
              ) : (
                <>No rules match your filters.</>
              )}
            </div>
          ) : (
            <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
              {filtered.map((p) => (
                <PolicyListRow
                  key={p.id}
                  policy={p}
                  users={users}
                  agents={agents}
                  agentPools={agentPools}
                  ticketQueues={ticketQueues}
                  customFieldCatalog={customFieldCatalog}
                  editable={editable}
                  selected={p.id === selectedId}
                  fullIndex={policies.findIndex((x) => x.id === p.id)}
                  fullCount={policies.length}
                  setTotalMatches={setTotalMatches}
                  onSelect={setSelectedId}
                  onReorder={reorderPolicies}
                />
              ))}
            </div>
          )}

          {filtered.length > 0 && (
            <div className="border-t border-border px-4 py-2 text-[11px] italic text-app-subtle">
              <span className="hidden lg:inline">Drag to reorder rules. </span>
              <span className="lg:hidden">Use the up/down buttons to reorder. </span>
              The first matching rule is applied.
            </div>
          )}
        </Card>

        {/* Right: editor panel */}
        <Card className="p-0">
          {selectedPolicy ? (
            <PolicyEditorPanel
              key={selectedPolicy.id}
              policy={selectedPolicy}
              policies={policies}
              users={users}
              agents={agents}
              reasonOptions={reasonOptions}
              topicOptions={topicOptions}
              agentPools={agentPools}
              ticketQueues={ticketQueues}
              customFieldCatalog={customFieldCatalog}
              editable={editable}
              ruleNumber={selectedRuleNumber}
              onSave={updatePolicy}
              onDelete={deletePolicy}
              onToggleActive={togglePolicyActive}
            />
          ) : (
            <div className="flex h-full min-h-[320px] items-center justify-center px-6 py-10 text-center text-sm text-app-muted">
              Select a rule on the left, or click{" "}
              <span className="mx-1 font-medium text-app-text">+ New Draft</span>{" "}
              to create one.
            </div>
          )}
        </Card>
      </div>

      {designerOpen && (
        <EscalationPolicyDesignerPane
          isDraft={editable}
          onClose={() => setDesignerOpen(false)}
          policies={policies}
          users={users}
          agents={agents}
          agentPools={agentPools}
          reasonOptions={reasonOptions}
          topicOptions={topicOptions}
          customFieldCatalog={customFieldCatalog}
        />
      )}
    </AppShell>
  )
}
